import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, tap, map, switchMap, catchError, of } from 'rxjs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  CalistActions,
  calistFeature,
  createCalistDataEffects,
  createCalistDeleteEffects,
  createCalistDrilldownEffects,
  createCalistEditEffects,
} from './calist.feature';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { caExpirationStatus, caNearExpirationCategory } from './calist.types';
import { CalistDataService } from 'src/app/shared/store/features/calist-data/calist-data.service';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import {
  numberComparator,
  stringComparator,
} from 'src/app/shared/util/comparators';
import { CA } from 'src/app/shared/entities/ca';
import { GridDataSourceState } from 'src/app/shared/store/grid-data/grid-data-source-state';
import { pageDataToGridData } from 'src/app/shared/store/grid-data/page-data-to-grid-data';

@Injectable()
export class CalistEffects {
  private action$ = inject(Actions);
  private router = inject(Router);
  private store = inject(Store);
  private langService = inject(LangService);
  private labels = this.langService.lang.features.calist;
  private toastrService = inject(ToastrService);
  private apiService = inject(ApiService);
  private dialogService = inject(DialogService);
  private calistDataService = inject(CalistDataService);

  private gridDataSourceState$: Observable<GridDataSourceState<CA>> =
    pageDataToGridData<CA>({
      filterChecks: {
        caName: (ca, filter) =>
          (ca.caName ?? '').toLowerCase().includes(filter.toLowerCase()),
        status: (ca, filter) => caExpirationStatus(ca) === filter,
        expiry: (ca, filter) => caNearExpirationCategory(ca) === filter,
      },
      comparisons: {
        caName: stringComparator(ca => ca.caName ?? ''),
        status: stringComparator(caExpirationStatus),
        expiry: numberComparator(ca => ca.expireTime ?? 0),
      },
    })(
      this.calistDataService.state$,
      this.store.select(calistFeature.selectGridDataParams),
    );

  private dataEffects = createCalistDataEffects(
    this.action$,
    this.store,
    this.gridDataSourceState$,
    this.router,
    {
      requestData: () => this.calistDataService.ensure(),
      refreshData: () => {
        /* Do nothing: refresh not supported */
      },
    },
  );

  private drilldownEffects = createCalistDrilldownEffects(
    this.action$,
    this.router,
  );

  // TODO should be moved to Calist Data
  private deleteEffects = createCalistDeleteEffects(this.action$, {
    deleteItem: item => this.apiService.deleteCA(item),
    onSuccess: item =>
      // If successfully deleted, update local copy
      // Reloading should not be needed
      this.calistDataService.transform(cas =>
        cas.filter(ca => ca.caName !== item.caName),
      ),
    notifyError: (ca, error) =>
      this.toastrService.error(
        extractErrorMessage(error) ||
          this.labels.toasts.defaultDeletionError(ca),
      ),
    notifySuccess: ca =>
      this.toastrService.success(this.labels.toasts.deletionSuccess(ca)),
  });

  private editEffects = createCalistEditEffects(this.action$, this.router, {
    saveItem: (originalCA, dto) =>
      this.apiService
        .updateCa(originalCA.caName ?? '', dto)
        .pipe(
          tap(editedCA =>
            this.calistDataService.transform(currentData =>
              currentData.map(item =>
                item.caName === editedCA.caName ? editedCA : item,
              ),
            ),
          ),
        ),
    confirmCancel: () =>
      this.dialogService.confirm({
        text: this.labels.actions.edit.confirmation,
      }),
    notifyFail: (_, __, error) =>
      this.toastrService.error(
        extractErrorMessage(error) || this.labels.actions.edit.savingError,
      ),
  });

  dataRequest$ = this.dataEffects.dataRequest$;
  dataUpdated$ = this.dataEffects.dataUpdated$;
  navigated$ = this.dataEffects.navigated$;
  refresh$ = this.dataEffects.refresh$;
  applyFilter$ = this.dataEffects.applyFilter$;
  applySorting$ = this.dataEffects.applySorting$;
  drilldown$ = this.drilldownEffects.drilldown$;
  backfromDrilldown$ = this.drilldownEffects.backFromDrilldown$;
  delete$ = this.deleteEffects.delete$;
  deleteSuccess$ = this.deleteEffects.deleteSuccess$;
  deleteFail$ = this.deleteEffects.deleteFail$;
  cancelEdit$ = this.editEffects.cancel$;
  doCancelEdit$ = this.editEffects.doCancel$;
  edit$ = this.editEffects.edit$;
  saveEdit$ = this.editEffects.save$;
  saveFail$ = this.editEffects.saveFail$;
  saveSuccess$ = this.editEffects.saveSuccess$;

  toastOnError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CalistActions.dataLoadFailure),
        tap(() => this.toastrService.error(this.labels.toasts.fetchingError)),
      ),
    { dispatch: false },
  );

  reloadCaList$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CalistActions.reload),
        tap(() => {
          this.calistDataService.load();
        }),
      ),
    { dispatch: false },
  );

  renew$ = createEffect(() =>
    this.action$.pipe(
      ofType(CalistActions.renew),
      switchMap(action =>
        this.apiService.renewCA(action.ca).pipe(
          map(() => CalistActions.renewSuccess(action)),
          catchError(error =>
            of(
              CalistActions.renewFail({
                ca: action.ca,
                errorMessage:
                  extractErrorMessage(error) ||
                  this.labels.details.actions.renew.defaultFailToast(
                    action.ca.caName ?? '',
                  ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  reloadOnRenewSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(CalistActions.renewSuccess),
      map(() => CalistActions.reload()),
    ),
  );

  toastOnRenewFail$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CalistActions.renewFail),
        tap(action => this.toastrService.error(action.errorMessage)),
      ),
    { dispatch: false },
  );

  toastOnRenewSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CalistActions.renewSuccess),
        tap(action =>
          this.toastrService.success(
            this.labels.details.actions.renew.successToast(
              action.ca.caName ?? '',
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  revoke = createEffect(() =>
    this.action$.pipe(
      ofType(CalistActions.revoke),
      switchMap(action =>
        this.apiService
          .revokeCA(action.ca, action.reason, action.revokeLeafCertificates)
          .pipe(
            map(() => CalistActions.revokeSuccess(action)),
            catchError(error =>
              of(
                CalistActions.revokeFail({
                  ca: action.ca,
                  errorMessage:
                    extractErrorMessage(error) ||
                    this.labels.details.actions.revoke.defaultFailToast(
                      action.ca.caName ?? '',
                    ),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  reloadOnRevokeSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(CalistActions.revokeSuccess),
      map(() => CalistActions.reload()),
    ),
  );

  toastOnRevokeFail$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CalistActions.revokeFail),
        tap(action => this.toastrService.error(action.errorMessage)),
      ),
    { dispatch: false },
  );

  toastOnRevokeSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CalistActions.revokeSuccess),
        tap(action =>
          this.toastrService.success(
            this.labels.details.actions.revoke.successToast(
              action.ca.caName ?? '',
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  generateCrl$ = createEffect(() =>
    this.action$.pipe(
      ofType(CalistActions.generateCrl),
      switchMap(action =>
        this.apiService.generateCrlBase64(action.ca?.caName || '').pipe(
          map(crl => CalistActions.generateCrlSuccess({ ...action, crl })),
          catchError(error =>
            of(
              CalistActions.generateCrlFail({
                ca: action.ca,
                errorMessage:
                  extractErrorMessage(error) ||
                  this.labels.details.actions.generateCrl.defaultFailToast(
                    action.ca.caName ?? '',
                  ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  toastOnGenerateCrlFail$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CalistActions.generateCrlFail),
        tap(action => this.toastrService.error(action.errorMessage)),
      ),
    { dispatch: false },
  );
}
